import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import { useAdapterSubscription, ConfigureAdapter, createAdapterContext, AdapterContext, useAdapterContext, selectAdapterConfigurationStatus, getIsFeatureEnabled, getFlagVariation, ToggleFeature as ToggleFeature$1, setDisplayName, wrapDisplayName, DEFAULT_FLAG_PROP_KEY, DEFAULT_FLAGS_PROP_KEY } from '@flopflip/react';
export { ReconfigureAdapter as ReconfigureFlopFlip, useAdapterReconfiguration } from '@flopflip/react';
import { adapterIdentifiers, AdapterSubscriptionStatus, AdapterConfigurationStatus } from '@flopflip/types';
import React, { createContext, useMemo, useCallback, useDebugValue, useContext } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';

function createStore(initialState) {
  let state = initialState;
  const getSnapshot = () => state;
  const listeners = new Set();
  function setState(fn) {
    state = fn(state);
    listeners.forEach(listener => {
      listener();
    });
  }
  function subscribe(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }
  return {
    getSnapshot,
    setState,
    subscribe
  };
}

const createIntialFlagsContext = (adapterIdentifiers, initialFlags) => Object.fromEntries(Object.values(adapterIdentifiers).map(adapterInterfaceIdentifier => [adapterInterfaceIdentifier, initialFlags]));
const FlagsContext = /*#__PURE__*/createContext(createIntialFlagsContext(adapterIdentifiers, {}));

const store = createStore({
  status: {},
  flags: {}
});
const useFlagsState = _ref => {
  let adapterIdentifiers = _ref.adapterIdentifiers;
  const flags = useSyncExternalStore(store.subscribe, () => store.getSnapshot().flags, () => store.getSnapshot().flags);
  const updateFlags = useCallback(flagsChange => {
    store.setState(prevState => {
      let nextState;
      if (flagsChange.id) {
        nextState = _objectSpread(_objectSpread({}, prevState), {}, {
          flags: _objectSpread(_objectSpread({}, prevState.flags), {}, {
            [flagsChange.id]: _objectSpread(_objectSpread({}, prevState.flags[flagsChange.id]), flagsChange.flags)
          })
        });
        return nextState;
      }
      nextState = _objectSpread(_objectSpread({}, prevState), {}, {
        flags: _objectSpread(_objectSpread({}, prevState.flags), Object.fromEntries(adapterIdentifiers.map(adapterInterfaceIdentifier => [adapterInterfaceIdentifier, _objectSpread(_objectSpread({}, prevState.flags[adapterInterfaceIdentifier]), flagsChange.flags)])))
      });
      return nextState;
    });
  }, [adapterIdentifiers]);
  return [flags, updateFlags];
};
const useStatusState = _ref2 => {
  let adapterIdentifiers = _ref2.adapterIdentifiers;
  const status = useSyncExternalStore(store.subscribe, () => store.getSnapshot().status, () => store.getSnapshot().status);
  const setStatus = useCallback(statusChange => {
    store.setState(prevState => {
      let nextState;
      if (statusChange.id) {
        nextState = _objectSpread(_objectSpread({}, prevState), {}, {
          status: _objectSpread(_objectSpread({}, prevState.status), {}, {
            [statusChange.id]: _objectSpread(_objectSpread({}, prevState.status[statusChange.id]), statusChange.status)
          })
        });
        return nextState;
      }
      nextState = _objectSpread(_objectSpread({}, prevState), {}, {
        status: _objectSpread(_objectSpread({}, prevState.status), Object.fromEntries(adapterIdentifiers.map(adapterInterfaceIdentifier => [adapterInterfaceIdentifier, _objectSpread(_objectSpread({}, prevState.status[adapterInterfaceIdentifier]), statusChange.status)])))
      });
      return nextState;
    });
  }, [adapterIdentifiers]);
  return [status, setStatus];
};
function Configure(_ref3) {
  let children = _ref3.children,
    _ref3$shouldDeferAdap = _ref3.shouldDeferAdapterConfiguration,
    shouldDeferAdapterConfiguration = _ref3$shouldDeferAdap === void 0 ? false : _ref3$shouldDeferAdap,
    _ref3$defaultFlags = _ref3.defaultFlags,
    defaultFlags = _ref3$defaultFlags === void 0 ? {} : _ref3$defaultFlags,
    adapter = _ref3.adapter,
    adapterArgs = _ref3.adapterArgs;
  const adapterIdentifiers = useMemo(() => [adapter.id], [adapter.id]);
  const _useFlagsState = useFlagsState({
      adapterIdentifiers
    }),
    _useFlagsState2 = _slicedToArray(_useFlagsState, 2),
    flags = _useFlagsState2[0],
    updateFlags = _useFlagsState2[1];
  const _useStatusState = useStatusState({
      adapterIdentifiers
    }),
    _useStatusState2 = _slicedToArray(_useStatusState, 2),
    status = _useStatusState2[0],
    updateStatus = _useStatusState2[1];
  // NOTE:
  //   Using this prevents the callbacks being invoked
  //   which would trigger a setState as a result on an unmounted
  //   component.
  const getHasAdapterSubscriptionStatus = useAdapterSubscription(adapter);
  const handleUpdateFlags = useCallback(flagsChange => {
    if (getHasAdapterSubscriptionStatus(AdapterSubscriptionStatus.Unsubscribed)) {
      return;
    }
    updateFlags(flagsChange);
  }, [updateFlags, getHasAdapterSubscriptionStatus]);
  const handleUpdateStatus = useCallback(statusChange => {
    if (getHasAdapterSubscriptionStatus(AdapterSubscriptionStatus.Unsubscribed)) {
      return;
    }
    updateStatus(statusChange);
  }, [updateStatus, getHasAdapterSubscriptionStatus]);
  return /*#__PURE__*/React.createElement(FlagsContext.Provider, {
    value: flags
  }, /*#__PURE__*/React.createElement(ConfigureAdapter, {
    adapter: adapter,
    adapterArgs: adapterArgs,
    adapterStatus: status,
    defaultFlags: defaultFlags,
    shouldDeferAdapterConfiguration: shouldDeferAdapterConfiguration,
    onFlagsStateChange: handleUpdateFlags,
    onStatusStateChange: handleUpdateStatus
  }, children));
}
Configure.displayName = 'ConfigureFlopflip';

const defaultProps = {
  adapterIdentifiers: ['test'],
  status: _objectSpread({}, Object.fromEntries(Object.values(adapterIdentifiers).map(adapterInterfaceIdentifier => [adapterInterfaceIdentifier, {
    subscriptionStatus: AdapterSubscriptionStatus.Subscribed,
    configurationStatus: AdapterConfigurationStatus.Configured
  }])))
};
function TestProvider(_ref) {
  let _ref$adapterIdentifie = _ref.adapterIdentifiers,
    adapterIdentifiers = _ref$adapterIdentifie === void 0 ? defaultProps.adapterIdentifiers : _ref$adapterIdentifie,
    reconfigure = _ref.reconfigure,
    flags = _ref.flags,
    children = _ref.children,
    _ref$status = _ref.status,
    status = _ref$status === void 0 ? defaultProps.status : _ref$status;
  const adapterContextValue = createAdapterContext(adapterIdentifiers, reconfigure, status);
  const flagsContextValue = createIntialFlagsContext(
  // @ts-expect-error Can not remember. Sorry to myself.
  adapterIdentifiers, flags);
  return /*#__PURE__*/React.createElement(AdapterContext.Provider, {
    value: adapterContextValue
  }, /*#__PURE__*/React.createElement(FlagsContext.Provider, {
    value: flagsContextValue
  }, children));
}
TestProvider.displayName = 'TestProviderFlopFlip';

function useAdapterStatus() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    adapterIdentifiers = _ref.adapterIdentifiers;
  const _useAdapterContext = useAdapterContext(),
    status = _useAdapterContext.status;
  const adapterStatus = selectAdapterConfigurationStatus(status, adapterIdentifiers);
  useDebugValue({
    adapterStatus
  });
  return adapterStatus;
}

const useFlagsContext = () => useContext(FlagsContext);

function useAllFeatureToggles() {
  const adapterContext = useAdapterContext();
  const flagsContext = useFlagsContext();
  const reversedAdapterEffectIdentifiers = [...adapterContext.adapterEffectIdentifiers].reverse();
  return reversedAdapterEffectIdentifiers.reduce((_allFlags, adapterIdentifier) => _objectSpread(_objectSpread({}, _allFlags), flagsContext[adapterIdentifier]), {});
}

function useFeatureToggle(flagName) {
  let flagVariation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  const adapterContext = useAdapterContext();
  const flagsContext = useFlagsContext();
  const isFeatureEnabled = getIsFeatureEnabled(flagsContext, adapterContext.adapterEffectIdentifiers, flagName, flagVariation);
  useDebugValue({
    flagName,
    flagVariation,
    isEnabled: isFeatureEnabled
  });
  return isFeatureEnabled;
}

function useFeatureToggles(flags) {
  const adapterContext = useAdapterContext();
  const flagsContext = useFlagsContext();
  const requestedFlags = Object.entries(flags).reduce((previousFlags, _ref) => {
    let _ref2 = _slicedToArray(_ref, 2),
      flagName = _ref2[0],
      flagVariation = _ref2[1];
    const isFeatureEnabled = getIsFeatureEnabled(flagsContext, adapterContext.adapterEffectIdentifiers, flagName, flagVariation);
    return [...previousFlags, isFeatureEnabled];
  }, []);
  return requestedFlags;
}

function useFlagVariations(flagNames) {
  const adapterContext = useAdapterContext();
  const flagsContext = useFlagsContext();
  const flagVariations = flagNames.map(requestedVariation => getFlagVariation(flagsContext, adapterContext.adapterEffectIdentifiers, requestedVariation));
  return flagVariations;
}

function useFlagVariation(flagName) {
  const _useFlagVariations = useFlagVariations([flagName]),
    _useFlagVariations2 = _slicedToArray(_useFlagVariations, 1),
    flagVariation = _useFlagVariations2[0];
  return flagVariation;
}

const _excluded = ["flag", "variation"];
function ToggleFeature(_ref) {
  let flag = _ref.flag,
    variation = _ref.variation,
    remainingProps = _objectWithoutProperties(_ref, _excluded);
  const isFeatureEnabled = useFeatureToggle(flag, variation);
  return /*#__PURE__*/React.createElement(ToggleFeature$1, Object.assign({
    flag: flag,
    variation: variation
  }, remainingProps, {
    isFeatureEnabled: isFeatureEnabled
  }));
}
ToggleFeature.displayName = 'ToggleFeature';

function branchOnFeatureToggle(_ref, UntoggledComponent) {
  let flagName = _ref.flag,
    flagVariation = _ref.variation;
  return ToggledComponent => {
    function WrappedToggledComponent(ownProps) {
      const isFeatureEnabled = useFeatureToggle(flagName, flagVariation);
      if (isFeatureEnabled) return /*#__PURE__*/React.createElement(ToggledComponent, ownProps);
      if (UntoggledComponent) return /*#__PURE__*/React.createElement(UntoggledComponent, ownProps);
      return null;
    }
    return WrappedToggledComponent;
  };
}

function injectFeatureToggle(flagName) {
  let propKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_FLAG_PROP_KEY;
  return Component => {
    function WrappedComponent(ownProps) {
      const _useFlagVariations = useFlagVariations([flagName]),
        _useFlagVariations2 = _slicedToArray(_useFlagVariations, 1),
        flagVariation = _useFlagVariations2[0];
      const props = _objectSpread(_objectSpread({}, ownProps), {}, {
        [propKey]: flagVariation
      });
      return /*#__PURE__*/React.createElement(Component, props);
    }
    setDisplayName(wrapDisplayName(WrappedComponent, 'injectFeatureToggle'));
    return WrappedComponent;
  };
}

function injectFeatureToggles(flagNames) {
  let propKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_FLAGS_PROP_KEY;
  return Component => {
    function WrappedComponent(ownProps) {
      const flagVariations = useFlagVariations(flagNames);
      const flags = Object.fromEntries(flagNames.map((flagName, indexOfFlagName) => [flagName, flagVariations[indexOfFlagName]]));
      const props = _objectSpread(_objectSpread({}, ownProps), {}, {
        [propKey]: flags
      });
      return /*#__PURE__*/React.createElement(Component, props);
    }
    setDisplayName(wrapDisplayName(WrappedComponent, 'injectFeatureToggles'));
    return WrappedComponent;
  };
}

const version = "14.0.2";

export { Configure as ConfigureFlopFlip, TestProvider as TestProviderFlopFlip, ToggleFeature, branchOnFeatureToggle, injectFeatureToggle, injectFeatureToggles, useAdapterStatus, useAllFeatureToggles, useFeatureToggle, useFeatureToggles, useFlagVariation, useFlagVariations, version };
